import React, { useEffect, useRef } from "react";

/**
 * The website's ambient background (sales-pipeline AmbientBackground), on the
 * login page only: a slow WebGL mesh in the brand colours, washed at 34% over
 * the paper base, with a faint grain on top. The shader library loads with
 * this component, so no other page downloads it. The paper base alone shows
 * before the first frame, without WebGL2, and if the library fails to load.
 */

// Periwinkle, its tint, amber and terracotta. No paper stop: paper on paper reads as a dead zone.
const MESH_COLORS = ["#5b6cff", "#dfe2ff", "#e6a419", "#c9784f"];
const MESH_SPEED = 0.4;
const PAPER = "#f4efe4";

// The mesh dissolves into the paper at the top and bottom, where iOS Safari paints its bars.
const EDGE_FADE = "linear-gradient(180deg, transparent 0%, #000 9%, #000 88%, transparent 100%)";
const GRAIN = "radial-gradient(rgba(33, 29, 26, 0.025) 0.5px, transparent 0.6px)";

// Bleeds past the safe-area insets, so the paper also fills behind the notch and home indicator.
const BLEED: React.CSSProperties = {
  top: "calc(-1 * env(safe-area-inset-top, 0px))",
  right: "calc(-1 * env(safe-area-inset-right, 0px))",
  bottom: "calc(-1 * env(safe-area-inset-bottom, 0px))",
  left: "calc(-1 * env(safe-area-inset-left, 0px))",
  background: PAPER,
};

export const LoginBackground: React.FC = () => {
  const meshRef = useRef<HTMLDivElement>(null);

  // iOS Safari will not paint this fixed layer behind its status bar and bottom toolbar, and fills those strips
  // with the body's background instead. Paper there meets the mesh's faded edges without a seam (the website's
  // paper.css does the same). Restored on unmount, so no page inside the app turns paper.
  useEffect(() => {
    const { documentElement: html, body } = document;
    const previous = [html.style.backgroundColor, body.style.backgroundColor];
    html.style.backgroundColor = PAPER;
    body.style.backgroundColor = PAPER;
    return () => {
      [html.style.backgroundColor, body.style.backgroundColor] = previous;
    };
  }, []);

  useEffect(() => {
    const host = meshRef.current;
    if (!host) return;
    let cancelled = false;
    let stop: (() => void) | null = null;

    import("@paper-design/shaders")
      .then(({ ShaderMount, meshGradientFragmentShader, getShaderColorFromString, ShaderFitOptions }) => {
        if (cancelled) return;
        const colors = MESH_COLORS.map(getShaderColorFromString);
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
        const mount = new ShaderMount(
          host,
          meshGradientFragmentShader,
          {
            u_colors: colors,
            u_colorsCount: colors.length,
            u_distortion: 0.8,
            u_swirl: 0.5,
            u_grainMixer: 0.25,
            u_grainOverlay: 0.12,
            u_fit: ShaderFitOptions.cover,
            u_scale: 1,
            u_rotation: 0,
            u_originX: 0.5,
            u_originY: 0.5,
            u_offsetX: 0,
            u_offsetY: 0,
            u_worldWidth: 0,
            u_worldHeight: 0,
          },
          undefined,
          reduceMotion.matches ? 0 : MESH_SPEED
        );
        const onMotionChange = (event: MediaQueryListEvent) => mount.setSpeed(event.matches ? 0 : MESH_SPEED);
        reduceMotion.addEventListener("change", onMotionChange);
        stop = () => {
          reduceMotion.removeEventListener("change", onMotionChange);
          mount.dispose();
        };
      })
      .catch(() => {
        // No WebGL2, or the library failed to load: the paper base is the background.
      });

    return () => {
      cancelled = true;
      stop?.();
    };
  }, []);

  return (
    <div aria-hidden="true" className="fixed z-0 pointer-events-none overflow-hidden" style={BLEED}>
      <div
        ref={meshRef}
        className="absolute inset-0 opacity-[0.34]"
        style={{ maskImage: EDGE_FADE, WebkitMaskImage: EDGE_FADE }}
      />
      <div className="absolute inset-0 opacity-50" style={{ backgroundImage: GRAIN, backgroundSize: "3px 3px" }} />
    </div>
  );
};
